import { useState, useEffect } from 'react'
import type { FiscalPeriodData } from '../../pages/SetupWizardPage'

interface Step3FiscalPeriodProps {
  initialData: FiscalPeriodData | null
  onSubmit: (data: FiscalPeriodData) => void
  onBack: () => void
  loading: boolean
}

export default function Step3FiscalPeriod({
  initialData,
  onSubmit,
  onBack,
  loading,
}: Step3FiscalPeriodProps) {
  const currentYear = new Date().getFullYear()

  const [formData, setFormData] = useState({
    start_date: initialData?.start_date || `${currentYear}-01-01`,
    end_date: initialData?.end_date || `${currentYear}-12-31`,
    name: initialData?.name || '',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [autoName, setAutoName] = useState(true)

  // Auto-generate name based on dates
  useEffect(() => {
    if (autoName && formData.start_date) {
      const year = new Date(formData.start_date).getFullYear()
      setFormData(prev => ({ ...prev, name: `Tilikausi ${year}` }))
    }
  }, [formData.start_date, autoName])

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.start_date) {
      newErrors.start_date = 'Alkamispäivä on pakollinen'
    }

    if (!formData.end_date) {
      newErrors.end_date = 'Päättymispäivä on pakollinen'
    }

    if (formData.start_date && formData.end_date) {
      const start = new Date(formData.start_date)
      const end = new Date(formData.end_date)

      if (end <= start) {
        newErrors.end_date = 'Päättymispäivän on oltava alkamispäivän jälkeen'
      }

      // Check period length (max 18 months for valid fiscal periods)
      const diffMonths = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth())
      if (diffMonths > 18) {
        newErrors.end_date = 'Tilikausi voi olla enintään 18 kuukautta'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setTouched({ start_date: true, end_date: true })

    if (validate()) {
      onSubmit({
        start_date: formData.start_date,
        end_date: formData.end_date,
        name: formData.name,
      })
    }
  }

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setTouched(prev => ({ ...prev, [field]: true }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const setPresetPeriod = (preset: 'calendar' | 'custom') => {
    if (preset === 'calendar') {
      setFormData({
        start_date: `${currentYear}-01-01`,
        end_date: `${currentYear}-12-31`,
        name: `Tilikausi ${currentYear}`,
      })
      setAutoName(true)
    }
  }

  // Calculate period length
  const getPeriodLength = () => {
    if (!formData.start_date || !formData.end_date) return null
    const start = new Date(formData.start_date)
    const end = new Date(formData.end_date)
    const diffTime = end.getTime() - start.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    const diffMonths = Math.round(diffDays / 30)
    return { days: diffDays, months: diffMonths }
  }

  const periodLength = getPeriodLength()

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Tilikauden määrittäminen</h2>
      <p className="text-gray-600 mb-6">Määritä ensimmäinen tilikausi. Yhdistyksellä voi olla useita peräkkäisiä tilikausia.</p>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Preset buttons */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setPresetPeriod('calendar')}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
          >
            Kalenterivuosi ({currentYear})
          </button>
        </div>

        {/* Date inputs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="start_date" className="block text-sm font-medium text-gray-700 mb-1">
              Alkamispäivä <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              id="start_date"
              value={formData.start_date}
              onChange={(e) => handleChange('start_date', e.target.value)}
              className={`
                w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2
                ${errors.start_date && touched.start_date
                  ? 'border-red-300 focus:ring-red-200'
                  : 'border-gray-300 focus:ring-blue-200 focus:border-blue-400'
                }
              `}
            />
            {errors.start_date && touched.start_date && (
              <p className="mt-1 text-sm text-red-600">{errors.start_date}</p>
            )}
          </div>

          <div>
            <label htmlFor="end_date" className="block text-sm font-medium text-gray-700 mb-1">
              Päättymispäivä <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              id="end_date"
              value={formData.end_date}
              onChange={(e) => handleChange('end_date', e.target.value)}
              className={`
                w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2
                ${errors.end_date && touched.end_date
                  ? 'border-red-300 focus:ring-red-200'
                  : 'border-gray-300 focus:ring-blue-200 focus:border-blue-400'
                }
              `}
            />
            {errors.end_date && touched.end_date && (
              <p className="mt-1 text-sm text-red-600">{errors.end_date}</p>
            )}
          </div>
        </div>

        {/* Period info */}
        {periodLength && periodLength.days > 0 && (
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
            <div className="flex items-center text-blue-800">
              <span className="mr-2">📅</span>
              <span className="font-medium">
                Tilikauden pituus: {periodLength.months} kuukautta ({periodLength.days} päivää)
              </span>
            </div>
          </div>
        )}

        {/* Period name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Tilikauden nimi
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => {
                handleChange('name', e.target.value)
                setAutoName(false)
              }}
              placeholder="Tilikausi 2026"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
            />
          </div>
          <div className="mt-2 flex items-center">
            <input
              type="checkbox"
              id="autoName"
              checked={autoName}
              onChange={(e) => setAutoName(e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="autoName" className="text-sm text-gray-600">
              Päivitä nimi automaattisesti päivämäärien mukaan
            </label>
          </div>
        </div>

        {/* Info box */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2">Tietoa tilikausista</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Tilikausi on kirjanpidon ajanjakso, jolta tulos lasketaan</li>
            <li>• Yhdistyksillä tilikausi on yleensä kalenterivuosi (1.1. - 31.12.)</li>
            <li>• Tilikausi voi olla myös muu 12 kuukauden jakso</li>
            <li>• Ensimmäinen tai viimeinen tilikausi voi olla 6-18 kuukautta</li>
            <li>• Voit lisätä uusia tilikausia myöhemmin asetuksista</li>
          </ul>
        </div>

        {/* Buttons */}
        <div className="flex justify-between pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onBack}
            disabled={loading}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
          >
            ← Takaisin
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Tallennetaan...
              </>
            ) : (
              <>Seuraava →</>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
